import React, { Component } from 'react';
import lodash from 'lodash';
import { Slider, IconButton, CircularProgress } from '@material-ui/core';
import { Crop, DeleteOutline, RotateLeft, Check } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import AvatarEditor from 'react-avatar-editor';

const styles = theme => ({
  editorContainer: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    width: '144px'
  },
  slider: {
    margin: theme.spacing(2)
  },
  borderRadius: '100px'
});

const CropToolbar = React.memo(({ zoom, handleChange, handleClose }) => (
  <>
    <Slider
      min={1}
      max={3}
      step={0.1}
      value={zoom}
      onChange={(_, z) => handleChange('zoom', z)}
    />
    <IconButton onClick={handleClose}>
      <Check />
    </IconButton>
  </>
));

const RegularToobar = React.memo(({ onCrop, onRotate, onDelete }) => (
  <>
    <IconButton onClick={onCrop}>
      <Crop />
    </IconButton>
    <IconButton onClick={onRotate}>
      <RotateLeft />
    </IconButton>
    {onDelete !== undefined && (
      <IconButton onClick={onDelete}>
        <DeleteOutline />
      </IconButton>
    )}
  </>
));

class ImageEditor extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      cropping: false,
      rotation: 0,
      zoom: 1,
      canvasWidth: this.props.cropWidth || 500,
      canvasHeight: this.props.cropHeight || 500,
      imageChange: false
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.zoom !== this.state.zoom && !this.state.imageChange) {
      this.setState({ imageChange: true });
    }
    if (prevState.rotation !== this.state.rotation && !this.state.imageChange) {
      this.setState({ imageChange: true });
    }
    // When reordering using `react-reorder` we need to force the
    // editor to update the editors so we know that the images changed
    // editor.
    if (prevProps.reloadCount !== this.props.reloadCount) {
      if (this.editor) {
        const canvas = this.editor.getImageScaledToCanvas();
        canvas.toBlob(
          blob => {
            this.props.handleChange(blob);
          },
          'image/jpeg',
          0.8
        );
      }
    }
  }

  handleChanges = (type, value) => {
    this.setState({ [type]: value });
  };

  handleClose = () => {
    if (this.props.handleClose) {
      this.props.handleClose();
    }
  };

  handleSave = () => {
    if (this.editor) {
      const canvas = this.editor.getImageScaledToCanvas();
      canvas.toBlob(
        blob => {
          this.props.handleEditorFinish(blob);
        },
        'image/jpeg',
        0.8
      );
    }
  };

  rotateLeft = () => {
    this.setState(state => ({
      ...state,
      rotation: state.rotation + 90
    }));
  };

  setEditorRef = editor => {
    this.editor = editor;
    // Send the editor ref to the Dialog or other component
    // encapsulating this component.
    if (this.props.setRef) {
      this.props.setRef(editor);
    }
  };

  imageReady = () => {
    this.setState({ loading: false });

    if (!this.props.handleChange) {
      return;
    }

    if (this.editor) {
      const canvas = this.editor.getImageScaledToCanvas();
      canvas.toBlob(
        blob => {
          this.props.handleChange(blob, true, this.state.imageChange);
        },
        'image/jpeg',
        0.8
      );
    }
  };

  imageMoved = () => {
    this.setState({ imageChange: true });
  };

  // Debounced function which will be extracting the image
  // in order to use it on the component that requires it.
  imageChange = () => {
    if (!this.props.handleChange || this.state.loading) {
      return;
    }

    if (this.editor) {
      const canvas = this.editor.getImageScaledToCanvas();
      canvas.toBlob(
        blob => {
          this.props.handleChange(blob, false, this.state.imageChange);
        },
        'image/jpeg',
        0.8
      );
    }
  };

  render() {
    const { classes } = this.props;

    return (
      <div className={classes.editorContainer} {...this.props.containerProps}>
        {this.state.loading ? <CircularProgress /> : null}
        <AvatarEditor
          className={classes.editor}
          ref={this.setEditorRef}
          style={{
            display: this.state.loading ? 'none' : 'unset',
            width: `${this.props.editorWidth || 300}px`,
            height: `'${this.props.editorHeight || 300}px`,
            pointerEvents: this.state.cropping ? 'all' : 'none'
          }}
          onImageReady={this.imageReady}
          onImageChange={lodash.debounce(this.imageChange, 1000)}
          onPositionChange={lodash.debounce(this.imageMoved, 200)}
          rotate={this.state.rotation}
          width={this.state.canvasWidth}
          height={this.state.canvasHeight}
          scale={this.state.zoom}
          image={this.props.image.url ? this.props.image.url : this.props.image}
          crossOrigin="anonymous"
        />
        <div className={classes.toolbar}>
          {this.state.cropping ? (
            <CropToolbar
              zoom={this.state.zoom}
              handleChange={this.handleChanges}
              handleClose={() => this.setState({ cropping: false })}
            />
          ) : (
            <RegularToobar
              onCrop={() => this.setState({ cropping: true })}
              onRotate={this.rotateLeft}
              onDelete={this.props.handleRemove}
            />
          )}
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(ImageEditor);
